import type { NuxtError } from '#app'
import type { Order } from '@brighterly/lib-core-types/orders/interfaces'
import type { CheckoutResponseData } from '@brighterly/lib-core-types/orders/types'
import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { useCheckoutStore } from '@stores/checkout'
import { useUpsellFlowStore } from '@stores/upsell-flow'

import { useCsRoutes } from '@composables/routes/cs-route.hook'
import { useToast } from '@hooks/toast/index.hook'

import { pick } from '@helpers/object'
import { mapCheckoutResponse } from '@utils/orders/checkout-response'
import { buildUpsellPlacementBody, getUpsellOfferUnavailableMessage, resolveUpsellPlacement } from '@utils/orders/upsell-placement'
import { show404 } from '@utils/pages/error'

interface CheckoutApiPayload {
  orderId: number
  route?: RouteLocationNormalizedGeneric
}

interface CheckoutApiOptions {
  onStart?: () => void
  onSuccess?: () => void
  onError?: () => void
  onOfferUnavailable?: (msg: string) => void
  onFinish?: () => void
}

export const useCheckoutApi = async (payload: CheckoutApiPayload, options?: CheckoutApiOptions): Promise<void | NuxtError> => {
  const { $axios } = useNuxtApp()

  const route = payload.route || useRoute()

  const checkoutStore = useCheckoutStore()
  const upsellFlowStore = useUpsellFlowStore()
  const toast = useToast()

  const upsellPlacement = resolveUpsellPlacement({
    orderId: payload.orderId,
    upsellOrder: checkoutStore.upsellOrder,
    flowPlacement: upsellFlowStore.getPlacement,
  })

  const query = pick({
    ...(route?.query || {}),
    signature: null,
    customer: null,
  })

  const url = useCsRoutes('orders.checkout', {
    '[order_id]': payload.orderId,
    ...query,
  })

  const set404 = (): NuxtError => {
    options?.onError?.()
    return show404()
  }

  const seedBillingZipCode = (orderZipCode: Order['zip_code']): void => {
    const alreadyTyped = !!checkoutStore.getBillingZipCode
    if (alreadyTyped || !orderZipCode) return

    checkoutStore.setBillingZipCode(orderZipCode)

    if (checkoutStore.getBillingCountry) {
      checkoutStore.setBillingConfirmed(true)
    }
  }

  if (!url) return set404()
  options?.onStart?.()

  try {
    const { data } = await $axios.post(url, buildUpsellPlacementBody(upsellPlacement))
    if (!data.success) return set404()

    const payload: CheckoutResponseData | null = data?.data?.order ? data.data : null
    if (!payload) return set404()

    const mapped = mapCheckoutResponse(payload)

    checkoutStore.setOrder(mapped.order)
    checkoutStore.setPaymentData(mapped.solidgateData)
    checkoutStore.setStripeClientSecret(mapped.stripeClientSecret)
    checkoutStore.setPaypalScriptUrl(mapped.paypalScriptUrl)
    checkoutStore.setAppliesForPromocode(mapped.appliesForPromocode)
    checkoutStore.setFurtherPayments(mapped.furtherPayments)
    seedBillingZipCode(mapped.order.zip_code)

    options?.onSuccess?.()

  } catch (err: any) {
    const offerUnavailableMessage = options?.onOfferUnavailable ? getUpsellOfferUnavailableMessage(err) : null

    if (offerUnavailableMessage) {
      toast.error(offerUnavailableMessage)
      options?.onOfferUnavailable?.(offerUnavailableMessage)
      return
    }

    const is404 = err?.response?.status === 404
    if (is404) return set404()

    toast.error(err?.response?.data?.message || err?.message || 'Unexpected error')
  } finally {
    options?.onFinish?.()
  }
}
